import { Button, Card, Divider, EmptyState } from '@ui/themed';
import { t } from 'i18next';
import { useMemo } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers } from '../../../context/page-context';
import { useCompany } from '../../companies/hooks/use-companies';
import type { SectionResponse } from '../types/api';
import { CompanyUserCard } from './company-user-card';
import { ContextCard } from './context-card';

export function SectionList({
  companyId,
  sections,
}: {
  companyId: string;
  sections: SectionResponse[];
}) {
  const { navigationHandler } = useHandlers();

  const {
    data: company,
    isLoading: companyLoading,
    isError: companyError,
    refetch: refecthCompany,
  } = useCompany(companyId);

  const sectionPaths: Record<string, string> = useMemo(() => {
    const sectionChildren = sections.reduce(
      (a, x) => {
        if (x.parent_id === null) {
          return a;
        }
        a[x.parent_id] = a[x.parent_id]?.concat(x) ?? [x];
        return a;
      },
      {} as Record<string, SectionResponse[]>,
    );

    const childPaths = (prefix: string[], section: SectionResponse): [string, string[]][] => [
      [section.id, prefix],
      ...(sectionChildren[section.id]?.flatMap((child) =>
        childPaths([...prefix, section.name], child),
      ) ?? []),
    ];

    // We want to override paths with longer paths
    const out: Record<string, string[]> = {};
    for (const section of sections) {
      for (const [id, path] of childPaths([], section)) {
        if ((out[id]?.length ?? 0) < path.length) {
          out[id] = [...path];
        }
      }
    }

    // Collapse paths into single string
    return Object.fromEntries(Object.entries(out).map(([id, path]) => [id, path.join(' > ')]));
  }, [sections]);

  return (
    <>
      <SparkSuspense
        onReload={refecthCompany}
        isError={companyError}
        isLoading={companyLoading}
        errorMessage={t`features:sections.components.section_list.company_load_error`}
      >
        {company && <ContextCard {...company} company></ContextCard>}
      </SparkSuspense>
      <div className="flex items-center justify-end">
        <div className="grow">
          <Divider />
        </div>
        <Button
          icon="add"
          onClick={() =>
            navigationHandler.navigate(`/customers/companies/${companyId}/sections/create`)
          }
          variant="bare"
        >
          {t('features:sections.components.section_list.add_button')}
        </Button>
      </div>
      {sections.length === 0 ? (
        <EmptyState
          title={t`features:sections.pages.sections_page.empty_state.title`}
          imageType="customerGroup"
        />
      ) : (
        sections.map((section) => (
          <ContextCard
            key={section.id}
            {...section}
            subtitle={sectionPaths[section.id]}
            onEdit={() =>
              navigationHandler.navigate(
                `/customers/companies/${companyId}/sections/edit/${section.id}`,
              )
            }
          >
            {section.company_users.length === 0 ? null : (
              <Card initialCollapsed>
                <Card.Header headingLevel="h4" collapsible icon="user">
                  {t('features:sections.components.section_list.company_users.title')}
                </Card.Header>
                <Card.Body>
                  {section.company_users.map((companyUser) => (
                    <CompanyUserCard key={companyUser.customer.id} {...companyUser} />
                  ))}
                </Card.Body>
              </Card>
            )}
          </ContextCard>
        ))
      )}
    </>
  );
}
